import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, Box, TextField } from "@mui/material";
import {
  DocumentCraftingLabel,
  DocumentCraftingLabelScope,
} from "../../../Models/documentCrafting";
import { useDocumentCraftingLabels } from "../../../Query/useDocumentCrafting";
import {
  LabelChip,
  LabelColorDot,
  LabelScopeIcon,
} from "../Common/LabelChips";

const isProject = (label: DocumentCraftingLabel) =>
  label.scope === DocumentCraftingLabelScope.PROJECT;

/**
 * Project and personal labels in one picker, grouped, with the group or
 * person icon. Archived labels are not offered, but a selected one (from a
 * link) stays visible, greyed.
 */
const LabelFilter: React.FC<{
  projectLabelIds: number[];
  personalLabelIds: number[];
  onChange: (projectLabelIds: number[], personalLabelIds: number[]) => void;
}> = ({ projectLabelIds, personalLabelIds, onChange }) => {
  const { t } = useTranslation("documentCrafting");
  const labels = useDocumentCraftingLabels(true);

  const { options, value } = useMemo(() => {
    const all = [
      ...(labels.data?.project ?? []),
      ...(labels.data?.personal ?? []),
    ];
    const selected = all.filter((label) =>
      isProject(label)
        ? projectLabelIds.indexOf(label.id) !== -1
        : personalLabelIds.indexOf(label.id) !== -1,
    );
    return {
      options: all.filter(
        (label) => !label.archived || selected.indexOf(label) !== -1,
      ),
      value: selected,
    };
  }, [labels.data, projectLabelIds, personalLabelIds]);

  return (
    <Autocomplete
      multiple
      size="small"
      limitTags={2}
      options={options}
      value={value}
      loading={labels.isLoading}
      filterSelectedOptions
      groupBy={(label) =>
        isProject(label)
          ? t("list.filters.projectLabels")
          : t("list.filters.personalLabels")
      }
      getOptionLabel={(label) => label.name}
      isOptionEqualToValue={(a, b) => a.id === b.id && a.scope === b.scope}
      onChange={(_, next) =>
        onChange(
          next.filter(isProject).map((label) => label.id),
          next.filter((label) => !isProject(label)).map((label) => label.id),
        )
      }
      noOptionsText={t("list.filters.noLabels")}
      loadingText={t("list.filters.loading")}
      renderOption={(props, label) => {
        const { key, ...optionProps } =
          props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
        return (
          <Box
            component="li"
            key={key}
            {...optionProps}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <LabelColorDot value={label.color} size={10} />
            <LabelScopeIcon scope={label.scope} />
            <Box
              component="span"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {label.name}
            </Box>
          </Box>
        );
      }}
      renderTags={(selected, getTagProps) =>
        selected.map((label, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <LabelChip
              key={key}
              label={label}
              {...tagProps}
              sx={{ maxWidth: 150 }}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={t("list.filters.labels")}
          placeholder={value.length ? undefined : t("list.filters.anyLabel")}
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
        />
      )}
    />
  );
};

export default LabelFilter;
